import { useTranslation } from 'react-i18next';
import { Badge, ErrorState } from '@/components/ui';
import { formatRM } from '@/features/billing/money';
import { isApiError } from '@/lib/api';
import type { LimitBillingAssessment } from './api';
import styles from './LimitBilling.module.css';

export function BillingLimitBanner({
  assessment,
  loading,
  error,
  confirmed,
  onConfirmedChange,
  onRetry,
}: {
  assessment?: LimitBillingAssessment;
  loading: boolean;
  error?: unknown;
  confirmed: boolean;
  onConfirmedChange: (confirmed: boolean) => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const shownError = error;
  const apiError = isApiError(shownError) ? shownError : undefined;

  return (
    <section
      className={styles.banner}
      aria-label={t('limitBilling.bannerTitle')}
    >
      <div className={styles.bannerHeading}>
        <strong>{t('limitBilling.bannerTitle')}</strong>
        {assessment && (
          <Badge
            tone={
              assessment.status === 'over_limit'
                ? 'warning'
                : assessment.status === 'within_limit'
                  ? 'success'
                  : 'warning'
            }
          >
            {t(`limitBilling.status.${assessment.status}`)}
          </Badge>
        )}
      </div>

      {loading ? (
        <p className={styles.note}>{t('limitBilling.assessing')}</p>
      ) : assessment ? (
        <>
          <p className={styles.note}>{t('limitBilling.splitBody')}</p>
          <dl className={styles.split}>
            <div>
              <dt>{t('limitBilling.eligibleTotal')}</dt>
              <dd>{formatRM(assessment.split.eligibleTotalSen)}</dd>
            </div>
            <div>
              <dt>{t('limitBilling.panelPays')}</dt>
              <dd>{formatRM(assessment.split.panelCoverageSen)}</dd>
            </div>
            <div className={styles.patientShare}>
              <dt>{t('limitBilling.patientPays')}</dt>
              <dd>{formatRM(assessment.split.patientPaySen)}</dd>
            </div>
          </dl>
          {assessment.nearLimitReasons.length > 0 && (
            <ul className={styles.reasons}>
              {assessment.nearLimitReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          )}
          <label className={styles.confirmation}>
            <input
              type="checkbox"
              checked={confirmed}
              disabled={!!shownError}
              onChange={(event) => onConfirmedChange(event.target.checked)}
            />
            <span>{t('limitBilling.confirmPatient')}</span>
          </label>
        </>
      ) : null}

      {shownError !== undefined && shownError !== null && (
        <ErrorState
          error={shownError}
          body={apiError?.message ?? t('limitBilling.assessmentFailed')}
          onRetry={onRetry}
        />
      )}
    </section>
  );
}
